<template>
  <div class="file-upload-container">
    <!-- Progress Bar Overlay -->
    <div
      v-if="downloading"
      class="progress-overlay"
    >
      <div class="progress-bar-container">
        <div
          class="progress-bar-fill"
          :style="{ width: downloadProgress + '%' }"
        />
      </div>
      <div class="progress-text">
        <b-spinner
          small
          class="mr-2"
        />
        İndiriliyor... {{ Math.round(downloadProgress) }}%
      </div>
    </div>

    <!-- Dosya Yükleme Alanı -->
    <b-card
      class="file-drop-zone"
      :class="{
        'border-primary bg-light': isDragOver,
        'has-file': selectedFile,
        'downloading': downloading
      }"
      no-body
      @drop="handleDrop"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @click="triggerFileInput"
    >
      <!-- Dosya Seçimi Input -->
      <input
        ref="fileInput"
        type="file"
        accept="*/*"
        class="d-none"
        :disabled="dataItem.id && !fileChange ? true : false"
        @change="handleFileSelect"
      >

      <!-- Boş Durum -->
      <div
        v-if="!dataItem.file_name ? true : false"
        class="upload-placeholder text-center py-5"
      >
        <div class="upload-icon mb-2">
          <feather-icon
            icon="UploadCloudIcon"
            size="50"
          />
        </div>
        <h4 class="upload-title text-dark mb-2">
          Dosya Seç
        </h4>
        <p class="upload-description text-muted mb-2">
          Dosyayı buraya sürükleyip bırakın veya seçmek için tıklayın
        </p>
      </div>

      <!-- Seçilen Dosya -->
      <div
        v-else
        class="selected-file p-4"
      >
        <div class="file-info d-flex align-items-center">
          <div class="file-icon mr-3">
            <feather-icon
              :icon="getFileIcon(dataItem.file_type)"
              size="40"
            />
          </div>
          <div class="file-details flex-grow-1">
            <h5 class="file-name mb-1">
              {{ dataItem.file_name }}
              <small
                v-if="downloading"
                class="text-muted ml-1"
              ><b-spinner small /> İndiriliyor..</small>
            </h5>
            <div class="file-meta d-flex gap-3">
              <small class="text-muted mr-1">{{ formatFileSize(dataItem.file_size) }}</small>
              <small class="text-muted">{{ dataItem.file_type || 'Bilinmeyen tür' }}</small>

            </div>
            <upload-progress
              v-if="uploading"
              :progress="uploadProgress"
              class="mt-1"
            />
          </div>
          <div class="file-actions">
            <b-button
              v-if="!uploading && !dataItem.id"
              variant="outline-danger"
              size="sm"
              @click.stop="removeFile"
            >
              <feather-icon icon="XIcon" />
              Kaldır
            </b-button>
            <b-button
              v-if="!uploading && dataItem.file_name && dataItem.id"
              variant="outline-info"
              size="sm"
              @click.stop="changeFile"
            >
              <feather-icon icon="RepeatIcon" />
              Güncel Doküman Yükle
            </b-button>
          </div>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import UploadProgress from '@/layouts/components/common/UploadProgress.vue'

export default {
  name: 'ServiceFilesFileCard',
  components: {
    UploadProgress,
    BCard,
    BButton,
    BSpinner,
  },
  data() {
    return {
      selectedFile: null,
      isDragOver: false,
      baseUrl: this.$store.state.app.baseURL,
      userData: JSON.parse(localStorage.getItem('userData')),
      downloading: false,
      downloadProgress: 0,
      progressInterval: null,
      fileChange: false,
    }
  },
  computed: {
    uploading() {
      return this.$store.getters['serviceFiles/uploading']
    },
    uploadProgress() {
      return this.$store.getters['serviceFiles/uploadProgress']
    },
    dataItem() {
      return this.$store.getters['serviceFiles/dataItem']
    },
  },
  beforeDestroy() {
    // Memory leak'i önlemek için interval'ı temizle
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
      this.progressInterval = null
    }
  },
  methods: {
    // Dosya seçimi tetikleme
    triggerFileInput() {
      if (this.dataItem.id && !this.fileChange) {
        // yeni sekmede yönlendir
        // window.open(`${this.baseUrl}/media/${this.userData.id_licenses}/services/${this.dataItem.file_name}`, '_blank')
        this.downloading = true
        this.downloadProgress = 0
        this.startProgressAnimation()
        this.$store.dispatch('downloads/createDownload', {
          item_id: this.dataItem.id,
          component: 'services',
        })
          .then(response => {
            if (response.status) {
              this.$store.dispatch('downloads/downloadFile', { token: response.token })
                .then(resp => {
                  if (resp) {
                    const respData = resp.data
                    if (respData.success) {
                      let blob

                      if (respData.chunks) {
                        // Chunk'ları birleştir
                        console.log('Chunk sayısı:', respData.total_chunks)

                        const byteArrays = []
                        respData.chunks.forEach(chunk => {
                          const byteCharacters = atob(chunk)
                          const byteNumbers = new Array(byteCharacters.length)

                          for (let i = 0; i < byteCharacters.length; i++) {
                            byteNumbers[i] = byteCharacters.charCodeAt(i)
                          }

                          byteArrays.push(new Uint8Array(byteNumbers))
                        })

                        // Tüm chunk'ları birleştir
                        const totalLength = byteArrays.reduce((acc, arr) => acc + arr.length, 0)
                        const result = new Uint8Array(totalLength)
                        let offset = 0

                        byteArrays.forEach(arr => {
                          result.set(arr, offset)
                          offset += arr.length
                        })

                        blob = new Blob([result], { type: respData.mime_type })
                      } else {
                        // Normal base64
                        const byteCharacters = atob(respData.content)
                        const byteNumbers = new Array(byteCharacters.length)

                        for (let i = 0; i < byteCharacters.length; i++) {
                          byteNumbers[i] = byteCharacters.charCodeAt(i)
                        }

                        const byteArray = new Uint8Array(byteNumbers)
                        blob = new Blob([byteArray], { type: respData.mime_type })
                      }
                      const url = window.URL.createObjectURL(blob)

                      const link = document.createElement('a')
                      link.href = url
                      link.download = respData.filename
                      link.click()

                      window.URL.revokeObjectURL(url)
                      this.completeDownload()
                    }
                  }
                })
                .catch(() => {
                  this.completeDownload()
                })
            } else {
              this.completeDownload()
            }
          })
          .catch(() => {
            this.completeDownload()
          })
      } else {
        this.$refs.fileInput.click()
      }
    },

    // Dosya seçimi işleme
    handleFileSelect(event) {
      const files = Array.from(event.target.files)
      if (files.length > 0) {
        // eslint-disable-next-line prefer-destructuring
        this.selectedFile = files[0]
        this.dataItem.file_name = this.selectedFile.name
        this.dataItem.file_size = this.selectedFile.size
        this.dataItem.file_type = this.selectedFile.type
        this.dataItem.upload_file = this.selectedFile
        if (this.fileChange) this.fileChange = false
      }
      // Input'u temizle
      event.target.value = ''
    },

    // Sürükleme olayları
    handleDragOver(event) {
      event.preventDefault()
      if (this.dataItem.id && !this.fileChange) return
      this.isDragOver = true
    },

    handleDragLeave(event) {
      event.preventDefault()
      if (this.dataItem.id && !this.fileChange) return
      this.isDragOver = false
    },

    handleDrop(event) {
      event.preventDefault()
      this.isDragOver = false
      if (this.dataItem.id && !this.fileChange) return

      const files = Array.from(event.dataTransfer.files)
      if (files.length > 0) {
        // eslint-disable-next-line prefer-destructuring
        this.selectedFile = files[0]
        this.dataItem.file_name = this.selectedFile.name
        this.dataItem.file_size = this.selectedFile.size
        this.dataItem.file_type = this.selectedFile.type
        this.dataItem.upload_file = this.selectedFile
        if (this.fileChange) this.fileChange = false
      }
    },

    // Dosya kaldırma
    removeFile() {
      this.selectedFile = null
      this.dataItem.file_name = null
      this.dataItem.prev_version = null
      this.dataItem.CurrentVersion = null
    },
    changeFile() {
      this.selectedFile = null
      this.dataItem.file_name = null
      this.fileChange = true
      if (this.dataItem.CurrentVersion) {
        this.dataItem.prev_version = this.dataItem.CurrentVersion.id
      }
      this.dataItem.CurrentVersion = null
    },
    // Dosya boyutunu formatlama
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'

      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))

      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
    },

    // Dosya türüne göre ikon belirleme
    getFileIcon(type) {
      if (!type) return 'fFileTextIcon'

      if (type.startsWith('image/')) return 'ImageIcon'
      if (type.startsWith('video/')) return 'VideoIcon'
      if (type.startsWith('audio/')) return 'Volume2Icon'
      if (type.includes('pdf')) return 'FileIcon'
      if (type.includes('word') || type.includes('document')) return 'FileTextIcon'
      if (type.includes('excel') || type.includes('spreadsheet')) return 'GridIcon'
      if (type.includes('powerpoint') || type.includes('presentation')) return 'AirplayIcon'
      if (type.includes('zip') || type.includes('rar') || type.includes('archive')) return 'FolderIcon'
      if (type.includes('text/')) return 'FileTextIcon'

      return 'FileTextIcon'
    },

    // Seçilen dosyayı döndürme (parent component için)
    getSelectedFile() {
      return this.selectedFile
    },
    removeSelectedFile() {
      this.selectedFile = null
    },

    // Progress bar animasyonu başlatma
    startProgressAnimation() {
      this.downloadProgress = 0
      this.progressInterval = setInterval(() => {
        if (this.downloadProgress < 90) {
          // Gerçekçi bir progress animasyonu için rastgele artış
          const increment = Math.random() * 15 + 5
          this.downloadProgress = Math.min(this.downloadProgress + increment, 90)
        }
      }, 200)
    },

    // İndirme tamamlandığında progress'i bitirme
    completeDownload() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval)
        this.progressInterval = null
      }

      // Progress'i 100%'e tamamla
      this.downloadProgress = 100

      // Kısa bir süre sonra progress bar'ı gizle
      setTimeout(() => {
        this.downloading = false
        this.downloadProgress = 0
      }, 500)
    },
  },
}
</script>

<style lang="scss" scoped>
.file-upload-container {
  width: 100%;
  position: relative;
}

// Progress Bar Overlay
.progress-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(2px);
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 0.375rem;
}

.progress-bar-container {
  width: 80%;
  height: 8px;
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 1rem;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #007bff, #0056b3);
  border-radius: 4px;
  transition: width 0.3s ease;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background-image: linear-gradient(
            -45deg,
            rgba(255, 255, 255, 0.2) 25%,
            transparent 25%,
            transparent 50%,
            rgba(255, 255, 255, 0.2) 50%,
            rgba(255, 255, 255, 0.2) 75%,
            transparent 75%,
            transparent
    );
    background-size: 20px 20px;
    animation: progress-stripes 1s linear infinite;
  }
}

@keyframes progress-stripes {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 20px 0;
  }
}

.progress-text {
  color: #495057;
  font-weight: 500;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}

.file-drop-zone {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px dashed #dee2e6 !important;
  position: relative;

  &:hover {
    border-color: #007bff !important;
    background-color: #f8f9fa !important;
  }

  &.border-primary {
    border-color: #007bff !important;
    background-color: #e3f2fd !important;
    transform: scale(1.01);
  }

  &.has-file {
    .card-body {
      padding: 0;
    }
  }

  &.downloading {
    pointer-events: none;
    opacity: 0.7;
  }
}

.upload-placeholder {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.selected-file {
  .file-info {
    .file-name {
      margin-bottom: 0.5rem;
      word-break: break-word;
    }

    .file-meta {
      font-size: 0.875rem;
    }
  }
}

// Bootstrap override'ları
.card-header {
  background-color: #f8f9fa !important;
  border-bottom: 1px solid #dee2e6;
}
</style>
